import { gql, useQuery } from "@apollo/client";
import type { ICustomerOrdersQuery, IOrdersEdgesNode } from "../queries/customer-gql";
import { customerOrdersQuery } from "../queries/customer-gql";
import { useMemo } from "react";

interface IUseFindOrderProps {
  orderId: string | undefined;
  accessToken: string;
  cacheFirst?: number; // 캐시 조회
  fetchFirst?: number; // 네트워크 조회
}

interface IUseFindOrderReturn {
  order: IOrdersEdgesNode | null;
  loading: boolean;
  orderNotFound: boolean;
}

export default function useFindOrder({
  orderId,
  accessToken,
  cacheFirst = 10,
  fetchFirst = 250,
}: IUseFindOrderProps): IUseFindOrderReturn {
  // Step 1: 캐시에서 주문 찾기
  const { data: cachedData, loading: cachedDataLoading } = useQuery<ICustomerOrdersQuery>(
    gql(customerOrdersQuery.query),
    {
      // OrderHistory와 동일
      variables: {
        customerAccessToken: accessToken,
        first: cacheFirst,
        sortKey: "PROCESSED_AT",
        reverse: true,
      },
      fetchPolicy: "cache-only",
      skip: !orderId || fetchFirst === 1,
    },
  );

  const cachedOrder = useMemo(() => {
    if (!orderId || !cachedData?.customer.orders.edges) return null;

    return cachedData.customer.orders.edges.find(({ node }) => node.id.includes(`gid://shopify/Order/${orderId}`))
      ?.node;
  }, [orderId, cachedData]);

  // Step 2: 캐시에 없으면 네트워크에서 250개 조회
  const { data: ordersData, loading: ordersDataLoading } = useQuery<ICustomerOrdersQuery>(
    gql(customerOrdersQuery.query),
    {
      variables: {
        customerAccessToken: accessToken,
        first: fetchFirst,
        sortKey: "PROCESSED_AT",
        reverse: true,
      },
      fetchPolicy: "no-cache",
      skip: !orderId,
    },
  );

  // 캐시 또는 네트워크에서 주문 찾기
  const order = useMemo(() => {
    if (!orderId) return null;

    // cachedOrder 우선 사용
    if (cachedOrder) return cachedOrder as IOrdersEdgesNode;

    if (!ordersData?.customer.orders.edges) return null;

    // ordersData에서 찾기
    return ordersData.customer.orders.edges.find(({ node }) => node.id.includes(`gid://shopify/Order/${orderId}`))
      ?.node as IOrdersEdgesNode;
  }, [orderId, cachedOrder, ordersData]);

  // 250개 안에서 못 찾으면 orderNotFound 설정
  const orderNotFound = useMemo(() => {
    if (!orderId || cachedDataLoading || ordersDataLoading) return false;
    if (cachedOrder) return false;

    if (!ordersData?.customer.orders.edges) return false;

    const matchedOrder = ordersData.customer.orders.edges.find(({ node }) =>
      node.id.includes(`gid://shopify/Order/${orderId}`),
    );

    return !matchedOrder;
  }, [orderId, cachedOrder, ordersDataLoading, ordersData]);

  return {
    order,
    loading: ordersDataLoading || cachedDataLoading,
    orderNotFound, // 250개 안에서 못 찾음 -> 이때 useOreDetail 훅에서 Admin API 실행
  };
}
